import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldAlert, Activity, Clock, Hash, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * PROV.1: Verified Activity Badge
 * Displays provenance summary stats on the Living CV page.
 * Shows: event count, days tracked, activity types, chain verification status.
 */

const VerifiedActivityBadge = ({ compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const res = await fetch(`${API_URL}/api/provenance/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSummary(data);
        }
      } catch (err) {
        console.debug('Provenance summary fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [API_URL]);

  if (loading || !summary || summary.total_events === 0) return null;

  const { total_events, days_tracked, activity_type_count, chain_verified, chain_length } = summary;

  const ShieldIcon = chain_verified ? ShieldCheck : ShieldAlert;
  const verifiedColor = chain_verified ? '#22c55e' : '#ef4444';
  const verifiedText = chain_verified ? 'Chain Verified' : 'Chain Broken';

  if (compact) {
    return (
      <div
        data-testid="provenance-badge-compact"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
        style={{ borderColor: `${verifiedColor}40`, color: verifiedColor, backgroundColor: `${verifiedColor}08` }}
      >
        <ShieldIcon className="w-3 h-3" />
        <span>{total_events} verified entries</span>
      </div>
    );
  }

  return (
    <div data-testid="provenance-badge" className="rounded-xl border border-gray-800 bg-gray-900/50 overflow-hidden">
      {/* Header — always visible */}
      <button
        data-testid="provenance-badge-toggle"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${verifiedColor}15` }}
          >
            <ShieldIcon className="w-4 h-4" style={{ color: verifiedColor }} />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-white">Verified Activity</p>
            <p className="text-xs text-gray-400">
              {total_events} entries over {days_tracked} day{days_tracked !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${verifiedColor}15`, color: verifiedColor }}
          >
            {verifiedText}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div data-testid="provenance-badge-details" className="px-4 pb-4 pt-1 border-t border-gray-800">
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="text-center p-2.5 bg-gray-800/30 rounded-lg">
              <Activity className="w-4 h-4 mx-auto mb-1 text-gray-400" />
              <p className="text-lg font-bold text-white">{total_events}</p>
              <p className="text-xs text-gray-500">Entries</p>
            </div>
            <div className="text-center p-2.5 bg-gray-800/30 rounded-lg">
              <Clock className="w-4 h-4 mx-auto mb-1 text-gray-400" />
              <p className="text-lg font-bold text-white">{days_tracked}</p>
              <p className="text-xs text-gray-500">Days Tracked</p>
            </div>
            <div className="text-center p-2.5 bg-gray-800/30 rounded-lg">
              <Hash className="w-4 h-4 mx-auto mb-1 text-gray-400" />
              <p className="text-lg font-bold text-white">{activity_type_count}</p>
              <p className="text-xs text-gray-500">Activity Types</p>
            </div>
          </div>

          {/* Event type breakdown */}
          {summary.event_types && Object.keys(summary.event_types).length > 0 && (
            <div className="mt-3 space-y-1.5">
              {Object.entries(summary.event_types).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400 capitalize">{type}s</span>
                  <span className="text-gray-300 font-medium">{count}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-800/50">
            <p className="text-xs text-gray-500 leading-relaxed">
              Each activity is hash-linked to the previous entry, creating a tamper-evident chain. 
              This proves your capabilities emerged from genuine activity over time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifiedActivityBadge;
